import PropTypes from 'prop-types';
import { MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { CustomTextField } from '../../utils/StyledInputs';
import { DefaultOutlinedButton } from '../../utils/Buttons';

// Tabella tariffe del riepilogo pagamento — estratta a COMPORTAMENTO INVARIATO dalla logica
// finora duplicata in RegistrationPage e ProfilePage (righe editabili, importo "Altro", totale).
// Le uniche differenze tra i due flussi sono parametrizzate:
//   - getReadonlyPrice(rate): prezzo mostrato in sola lettura (gestisce lo sconto giovani PD);
//   - showCurrencyColumn: visibilità della colonna "Valuta" (responsive in registrazione).
// Lo stato (disabledStates, otherValue) e gli handler restano nel componente padre: qui è "muta".
const OTHER_AMOUNTS = [0, 5, 10, 20, 50, 100, 200, 500];

export default function RatesTable({
  rates,
  disabledStates,
  otherValue,
  setOtherValue,
  onRateChange,
  onToggleEditable,
  getReadonlyPrice,
  totalPrice,
  showCurrencyColumn = true,
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">Tariffa</TableCell>
            <TableCell align="right">Costo</TableCell>
            {showCurrencyColumn && <TableCell align="center">Valuta</TableCell>}
            <TableCell align="center">Modificabile</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rates.map((rate, index) => (
            <TableRow key={rate.id}>
              <TableCell align="center">{rate.name}</TableCell>
              <TableCell sx={{ minWidth: '40px' }} align="right">
                {disabledStates[index] ? (
                  otherValue[index]?.isOther ? (
                    <CustomTextField
                      value={otherValue[index]?.value || ''}
                      onKeyDown={(e) => {
                        const allowedKeys = ['Backspace', 'Tab', '.', ','];
                        if (!allowedKeys.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const inputValue = e.target.value.replace(',', '.');
                        setOtherValue({
                          ...otherValue,
                          [index]: { ...otherValue[index], value: inputValue },
                        });
                        onRateChange(index, inputValue);
                      }}
                      label="Importo personalizzato"
                    />
                  ) : (
                    <CustomTextField
                      customStyles={{ width: '100px' }}
                      select
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue === -1) {
                          setOtherValue({
                            ...otherValue,
                            [index]: { isOther: true, value: '' },
                          });
                        } else {
                          setOtherValue({
                            ...otherValue,
                            [index]: { isOther: false, value: selectedValue },
                          });
                          onRateChange(index, selectedValue);
                        }
                      }}
                      label="Importo"
                      value={otherValue[index]?.value || ''}
                    >
                      {OTHER_AMOUNTS.map((amount) => (
                        <MenuItem key={amount} value={amount}>
                          {amount}
                        </MenuItem>
                      ))}
                      <MenuItem value={-1}>Altro</MenuItem>
                    </CustomTextField>
                  )
                ) : (
                  <Typography>{getReadonlyPrice(rate)}</Typography>
                )}
              </TableCell>
              {showCurrencyColumn && <TableCell align="center">{rate.currency}</TableCell>}
              <TableCell align="center">
                {rate.has_editable === 1 ? (
                  <DefaultOutlinedButton onClick={() => onToggleEditable(index)}>
                    {disabledStates[index] ? 'Salva' : 'Modifica'}
                  </DefaultOutlinedButton>
                ) : (
                  <DefaultOutlinedButton onClick={() => onToggleEditable(index)} disabled>
                    Fissa
                  </DefaultOutlinedButton>
                )}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              Totale
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '1em', fontWeight: 'bold' }}>
              {!Number.isNaN(totalPrice) ? totalPrice.toFixed(2) : 'Valore non valido'}
            </TableCell>
            {/* La cella "EUR" del totale è sempre presente in entrambi i flussi (anche quando la
                colonna Valuta è nascosta su mobile in registrazione): comportamento invariato. */}
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              EUR
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

RatesTable.propTypes = {
  rates: PropTypes.array.isRequired,
  disabledStates: PropTypes.array.isRequired,
  otherValue: PropTypes.object.isRequired,
  setOtherValue: PropTypes.func.isRequired,
  onRateChange: PropTypes.func.isRequired,
  onToggleEditable: PropTypes.func.isRequired,
  getReadonlyPrice: PropTypes.func.isRequired,
  totalPrice: PropTypes.number,
  showCurrencyColumn: PropTypes.bool,
};
